'use client'

import {
  BRAND_LOGO_IMAGE_HEIGHT,
  BRAND_LOGO_IMAGE_WIDTH,
} from '@/feature/brand/services/brand.constants'
import {
  PRODUCT_IMAGE_SIZE_DESKTOP,
  PRODUCT_IMAGE_SIZE_MOBILE,
  PRODUCT_IMAGE_SIZE_THUMBNAIL,
} from '@/feature/product/services/product.constants'
import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { pushToGtmDataLayer } from '@/lib/tracking/services/tracking.gtm'
import { Button } from '@/ui/buttons/button/Button.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { CloudflarePictureProduct } from '@/ui/media/cloudflare-picture-product/CloudflarePictureProduct.client'
import { CloudflarePicture } from '@/ui/media/cloudflare-picture/CloudflarePicture.client'
import { Picture } from '@/ui/media/picture/Picture.client'
import { generatePictureSource } from '@/ui/media/picture/Picture.service'
import VideoGallery, { type VideoGalleryRef } from '@/ui/media/video-gallery/VideoGallery.client'
import { VideoPlayButton, VideoPlayIcon } from '@/ui/media/video-play-button/VideoPlayButton.client'
import { Media, type Image as ImageType } from '@gql-generated/graphql'
import type { CSSProperties } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { Swiper as SwiperInstance } from 'swiper'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/scrollbar'
import 'swiper/css/thumbs'
import { Navigation, Scrollbar, Thumbs } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import './ProductImageSlider.css'

// Slide image size classes
const SLIDE_IMAGE_BASE_SIZE =
  'size-[16.875rem] object-cover desktop:size-auto desktop:max-h-[35rem]'
const SLIDE_IMAGE_FALLBACK_SIZE = 'object-contain desktop:size-full'

export type Slide = {
  mainImage: {
    alt?: string
    set?: Array<{
      media: Media
      url: string
    }>
  }
  thumbImage: {
    alt?: string
    set?: Array<{
      media: Media
      url: string
    }>
  }
  videoUrl?: string
}

export type ProductImageSliderProps = {
  brandLogo: ImageType | null | undefined
  slides: Slide[]
  className?: string
}

export function ProductImageSlider({
  brandLogo,
  slides,
  className,
}: ProductImageSliderProps): React.ReactElement | null {
  const { t } = useTranslation(['generic'])
  const swiperRef = useRef<SwiperInstance | null>(null)
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperInstance | null>(null)
  const [prevMainEl, setPrevMainEl] = useState<HTMLDivElement | null>(null)
  const [nextMainEl, setNextMainEl] = useState<HTMLDivElement | null>(null)
  const [prevThumbEl, setPrevThumbEl] = useState<HTMLDivElement | null>(null)
  const [nextThumbEl, setNextThumbEl] = useState<HTMLDivElement | null>(null)

  // Unique key to force Swiper re-mount when images change (e.g. variant switch),
  // ensuring correct initialization of the new slides.
  const slidesKey = useMemo(
    () => JSON.stringify(slides.map((s) => s.mainImage.set?.[0]?.url)),
    [slides],
  )

  useEffect(() => {
    if (swiperRef.current && !swiperRef.current.destroyed) {
      swiperRef.current.update()
      swiperRef.current.navigation?.update()
    }

    if (thumbsSwiper && !thumbsSwiper.destroyed) {
      thumbsSwiper.update()
      thumbsSwiper.navigation?.update()
    }
  }, [slidesKey, thumbsSwiper])

  const galleryRef = useRef<VideoGalleryRef>(null)
  const videoSlides = slides.filter((slide) => slide.videoUrl)
  const videoItems = videoSlides.map((slide) => ({ src: slide.videoUrl as string }))

  const openVideoGallery = (index: number) => {
    galleryRef.current?.openGallery(index)
  }

  if (slides.length === 0) {
    return null
  }

  return (
    <div
      className={twMerge(
        className,
        'flex h-auto w-full gap-4 desktop:h-[35rem] desktop:max-w-[49.5rem]',
      )}
    >
      <div className="relative hidden h-full w-10 justify-center desktop:flex">
        <Swiper
          onSwiper={setThumbsSwiper}
          direction="vertical"
          spaceBetween={16}
          slidesPerView="auto"
          modules={[Thumbs, Navigation]}
          className="swiper-container-thumbs size-full"
          slideToClickedSlide
          observer
          observeParents
          navigation={
            prevThumbEl && nextThumbEl
              ? {
                  prevEl: prevThumbEl,
                  nextEl: nextThumbEl,
                }
              : false
          }
        >
          {slides.map((slide, index) => (
            <SwiperSlide
              // eslint-disable-next-line react/no-array-index-key
              key={`slide-${index}`}
              className="!size-10 cursor-pointer overflow-hidden"
              onClick={() =>
                pushToGtmDataLayer({
                  event: 'gaEvent',
                  eventCategory: 'fiche article / produit',
                  eventAction: slide.videoUrl ? 'clic vidéo latérale' : 'clic image latérale',
                  eventLabel: index.toString(),
                })
              }
            >
              {slide.videoUrl ? ( // Do not use Cloudflare CDN for Video thumbnails
                <div className="relative size-full">
                  <Picture
                    image={generatePictureSource({
                      mobileUrl: slide.thumbImage.set?.[0]?.url ?? '',
                      alt: slide.thumbImage?.alt ?? '',
                    })}
                    mode="cover"
                    className="size-full"
                  />
                  <VideoPlayIcon />
                </div>
              ) : (
                <CloudflarePictureProduct
                  src={slide.mainImage.set?.[0]?.url}
                  alt={slide.mainImage?.alt}
                  width={PRODUCT_IMAGE_SIZE_THUMBNAIL}
                  className="size-full max-h-[16.875rem] desktop:max-h-[35rem]"
                />
              )}
            </SwiperSlide>
          ))}
        </Swiper>

        <div
          ref={setPrevThumbEl}
          className="swiper-button-prev-thumb absolute top-0 z-menu-arrows transform cursor-pointer"
        >
          <Button
            variant="tertiary"
            size="sm"
            isRound
            iconLeft={<Icon.ArrowUp size="sm" />}
            aria-label={t('generic:previous')}
          />
        </div>
        <div
          ref={setNextThumbEl}
          className="swiper-button-next-thumb absolute bottom-0 z-menu-arrows transform cursor-pointer"
        >
          <Button
            variant="tertiary"
            size="sm"
            isRound
            iconLeft={<Icon.ArrowDown size="sm" />}
            aria-label={t('generic:next')}
          />
        </div>
      </div>

      <div
        className="relative h-auto min-w-0 flex-1"
        style={
          {
            '--swiper-scrollbar-size': 'var(--slider-scrollbar-height)',
            '--swiper-scrollbar-bg-color': 'var(--scrollbar-track-color, var(--color-brand-2-200))',
            '--swiper-scrollbar-drag-bg-color':
              'var(--scrollbar-thumb-color, var(--color-primary))',
            '--swiper-scrollbar-sides-offset': '0%',
            '--swiper-scrollbar-bottom': '0',
          } as CSSProperties
        }
      >
        {brandLogo && (
          <div className="absolute left-0 top-0 z-menu-arrows hidden desktop:block">
            <CloudflarePicture
              className="h-5 w-[6.25rem]"
              width={BRAND_LOGO_IMAGE_WIDTH}
              height={BRAND_LOGO_IMAGE_HEIGHT}
              alt={brandLogo?.alt ?? undefined}
              src={brandLogo?.set?.[0]?.url}
              mode="contain"
            />
          </div>
        )}

        <Swiper
          allowTouchMove
          spaceBetween={10}
          modules={[Navigation, Thumbs, Scrollbar]}
          thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
          slidesPerView="auto"
          className="size-full"
          observer
          observeParents
          onSwiper={(swiper) => {
            swiperRef.current = swiper
          }}
          navigation={
            prevMainEl && nextMainEl
              ? {
                  prevEl: prevMainEl,
                  nextEl: nextMainEl,
                }
              : false
          }
          scrollbar={{
            enabled: true,
            draggable: true,
            el: '.swiper-scrollbar',
            hide: false,
            dragClass: 'swiper-scrollbar-drag',
          }}
          breakpoints={{
            1024: {
              allowTouchMove: false,
            },
          }}
        >
          {slides.map((slide, index) => (
            <SwiperSlide
              // eslint-disable-next-line react/no-array-index-key
              key={`slide-${index}`}
              className="!grid min-w-[16.875rem] !shrink place-items-center overflow-hidden py-1 desktop:!h-auto desktop:min-w-full desktop:py-0"
              onClick={() =>
                pushToGtmDataLayer({
                  event: 'gaEvent',
                  eventCategory: 'fiche article / produit',
                  eventAction: slide.videoUrl ? 'clic vidéo centrale' : 'image',
                  eventLabel: slide.videoUrl ? 'clic vidéo centrale' : 'clic image centrale',
                })
              }
            >
              {slide.videoUrl ? (
                <VideoPlayButton
                  onPress={() => openVideoGallery(videoSlides.indexOf(slide))}
                  thumbnailUrl={
                    slide.mainImage.set?.find((img) => img.media === Media.Desktop)?.url ||
                    slide.mainImage.set?.[0]?.url
                  }
                  alt={slide.mainImage?.alt}
                  iconSize={100}
                  className={twMerge(
                    SLIDE_IMAGE_BASE_SIZE,
                    !slide.mainImage.set?.length && SLIDE_IMAGE_FALLBACK_SIZE,
                  )}
                />
              ) : (
                <CloudflarePictureProduct
                  src={slide.mainImage.set?.[0]?.url}
                  alt={slide.mainImage.alt}
                  width={{
                    mobile: PRODUCT_IMAGE_SIZE_MOBILE,
                    desktop: PRODUCT_IMAGE_SIZE_DESKTOP,
                  }}
                  loadingStrategy={index === 0 ? 'eager' : 'lazy'}
                  fetchPriority={index === 0 ? 'high' : undefined}
                  className={twMerge(
                    SLIDE_IMAGE_BASE_SIZE,
                    'object-cover',
                    !slide.mainImage.set?.length && SLIDE_IMAGE_FALLBACK_SIZE,
                    !slide.mainImage.set?.length && 'object-contain',
                  )}
                />
              )}
            </SwiperSlide>
          ))}
          <div className="swiper-scrollbar m-0 after:absolute after:inset-[0] after:bg-current after:opacity-25 desktop:hidden" />
        </Swiper>

        <div className="z-menu-arrows desktop:hidden">
          <div
            ref={setPrevMainEl}
            className="swiper-button-prev-main absolute left-0 top-1/2 z-menu-arrows -translate-y-1/2 transform cursor-pointer"
          >
            <Button
              variant="tertiary"
              size="md"
              isRound
              iconLeft={<Icon.ArrowLeft size="md" />}
              aria-label={t('generic:previous')}
            />
          </div>
          <div
            ref={setNextMainEl}
            className="swiper-button-next-main absolute right-0 top-1/2 z-menu-arrows -translate-y-1/2 transform cursor-pointer"
          >
            <Button
              variant="tertiary"
              size="md"
              isRound
              iconLeft={<Icon.ArrowRight size="md" />}
              aria-label={t('generic:next')}
            />
          </div>
        </div>
      </div>
      {videoItems.length > 0 && <VideoGallery ref={galleryRef} videos={videoItems} />}
    </div>
  )
}
